import { Box, Typography, Container, useTheme } from '@mui/material';
import { useOidc } from '@nohara-dxdev/react-oidc';

const NotAuthorizedRoute = () => {
  const { user } = useOidc();
  const theme = useTheme();

  // Get email from auth user
  const userEmail = user?.email || '';

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme.palette.background.default,
        py: { xs: 2, sm: 3, md: 4 },
        px: { xs: 1, sm: 2 },
      }}
    >
      <Container maxWidth='md' sx={{ width: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: { xs: 2, sm: 2.5, md: 3 },
            px: { xs: 1, sm: 2 },
          }}
        >
          {/* Header Section */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: { xs: 0.5, sm: 1 },
            }}
          >
            {/* 403 Box - Red background with white text */}
            <Box
              sx={{
                backgroundColor: '#D32F2F', // Design specific color from SVG
                borderRadius: 1,
                width: { xs: '48px', sm: '100px', md: '120px' },
                height: { xs: '48px', sm: '100px', md: '120px' },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography
                component='div'
                sx={{
                  fontWeight: 400,
                  fontStyle: 'normal',
                  color: theme.palette.common.white,
                  fontSize: { xs: '24px', sm: '3rem', md: '3.75rem' },
                  lineHeight: { xs: '32px', sm: 1, md: 1 },
                }}
              >
                403
              </Typography>
            </Box>

            {/* Access Denied Text - Simple text below 403 box */}
            <Typography
              variant='h4'
              component='div'
              sx={{
                fontWeight: 400,
                color: theme.palette.text.primary,
                textAlign: 'center',
                fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' }, // h4 sizes
                lineHeight: { xs: '32px', sm: 1.25 }, // 32px lineHeight
              }}
            >
              アクセス拒否
            </Typography>
          </Box>

          {/* Warning Banner - Light red background with warning icon */}
          <Box
            sx={{
              backgroundColor: '#FFEBEE',
              borderRadius: 1,
              px: { xs: 2, sm: 3 },
              py: { xs: 1.5, sm: 2 },
              display: 'flex',
              alignItems: 'flex-start',
              gap: { xs: 1.5, sm: 2 },
              width: '100%',
            }}
          >
            <Box
              sx={{
                backgroundColor: '#B00020',
                borderRadius: '50%',
                width: { xs: 20, sm: 24, md: 28 },
                height: { xs: 20, sm: 24, md: 28 },
                minWidth: { xs: 20, sm: 24, md: 28 },
                minHeight: { xs: 20, sm: 24, md: 28 },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                alignSelf: 'center', // Center icon vertically with text
              }}
            >
              <Typography
                sx={{
                  color: theme.palette.common.white,
                  fontSize: { xs: 14, sm: 16, md: 18 },
                  fontWeight: 700,
                  lineHeight: 1,
                  fontFamily: theme.typography.fontFamily,
                }}
              >
                !
              </Typography>
            </Box>
            <Typography
              variant='body1'
              sx={{
                color: '#68000F', // Design specific color from SVG
                fontWeight: 500, // Medium weight per original design
                fontSize: { xs: '0.875rem', sm: '1rem' }, // body1/body2 sizes
                lineHeight: { xs: 1.43, sm: 1.5 }, // body1/body2 lineHeight
                flex: 1,
              }}
            >
              こちらのメールアドレスはBuildApp生コンデリバリーに登録されていません。システム管理者にお問い合わせください。
            </Typography>
          </Box>

          {/* Main Content Box - Red dashed border */}
          <Box
            sx={{
              border: '2px dashed #D32F2F', // Design specific color
              borderRadius: 1,
              px: { xs: 2, sm: 3, md: 4 },
              py: { xs: 2.5, sm: 3, md: 4 },
              backgroundColor: theme.palette.background.paper,
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: { xs: 2, sm: 2.5, md: 3 },
            }}
          >
            {/* No Access Rights Section */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: { xs: 0.75, sm: 1 },
              }}
            >
              {/* Red circle with white minus sign */}
              <Box
                sx={{
                  width: { xs: 48, sm: 56, md: 64 },
                  height: { xs: 48, sm: 56, md: 64 },
                  borderRadius: '50%',
                  border: {
                    xs: '3px solid #B00020',
                    sm: '4px solid #B00020',
                    md: '4px solid #B00020',
                  },
                  backgroundColor: 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Box
                  sx={{
                    width: { xs: 20, sm: 24, md: 28 },
                    height: { xs: 3, sm: 4, md: 4 },
                    backgroundColor: '#B00020',
                    borderRadius: 1,
                  }}
                />
              </Box>
              <Typography
                variant='h6'
                sx={{
                  color: theme.palette.text.primary,
                  fontWeight: 400,
                  fontSize: { xs: '1rem', sm: '1.125rem', md: '1.5rem' }, // h6 sizes
                  lineHeight: { xs: '32px', sm: 1.33 }, // 32px lineHeight
                  textAlign: 'center',
                }}
              >
                アクセス権限がありません
              </Typography>
            </Box>

            {/* Login Information Section */}
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: { xs: 1.5, sm: 2 },
              }}
            >
              <Typography
                variant='h6'
                sx={{
                  fontWeight: 500,
                  fontStyle: 'normal',
                  color: theme.palette.text.primary,
                  fontSize: { xs: '1rem', sm: '1.125rem', md: '1.5rem' }, // h6 sizes
                  lineHeight: { xs: '24px', sm: 1.5 }, // 24px (150%)
                  letterSpacing: { xs: '0.15px', sm: 'normal' },
                }}
              >
                ログイン情報
              </Typography>
              {/* Light gray box for login info */}
              <Box
                sx={{
                  backgroundColor: '#F9F9FF', // Design specific color
                  borderRadius: 1,
                  px: { xs: 1.5, sm: 2 },
                  py: { xs: 1.5, sm: 2 },
                  display: 'flex',
                  flexDirection: 'column',
                  gap: { xs: 0.75, sm: 1 },
                }}
              >
                <Typography
                  variant='body1'
                  sx={{
                    color: theme.palette.text.primary,
                    fontWeight: 400,
                    fontSize: { xs: '0.875rem', sm: '1rem' }, // body1/body2 sizes
                    lineHeight: { xs: 1.43, sm: 1.5 }, // body1/body2 lineHeight
                    wordBreak: 'break-word',
                  }}
                >
                  メールアドレス: {userEmail || 'N/A'}
                </Typography>
                <Typography
                  variant='body1'
                  sx={{
                    color: theme.palette.text.primary,
                    fontWeight: 400,
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    lineHeight: { xs: 1.43, sm: 1.5 },
                  }}
                >
                  アクセス権限:{' '}
                  <Box component='span' sx={{ color: '#B00020' }}>
                    なし
                  </Box>
                </Typography>
              </Box>
            </Box>

            {/* Solution Section */}
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: { xs: 1.5, sm: 2 },
              }}
            >
              <Typography
                variant='h6'
                sx={{
                  fontWeight: 500,
                  fontStyle: 'normal',
                  color: theme.palette.text.primary,
                  fontSize: { xs: '1rem', sm: '1.125rem', md: '1.5rem' }, // h6 sizes
                  lineHeight: { xs: '24px', sm: 1.5 }, // 24px (150%)
                  letterSpacing: { xs: '0.15px', sm: 'normal' },
                }}
              >
                解決方法
              </Typography>
              <Box
                component='ul'
                sx={{
                  backgroundColor: '#F9F9FF', // Design specific color
                  borderRadius: 1,
                  px: { xs: 1.5, sm: 2 },
                  py: { xs: 1.5, sm: 2 },
                  pl: { xs: 2.5, sm: 3 },
                  m: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: { xs: 1.25, sm: 1.5 },
                  '& li::marker': {
                    color: '#0047BA', // Design specific color for bullet points
                  },
                }}
              >
                <Typography
                  component='li'
                  variant='body1'
                  sx={{
                    color: theme.palette.text.primary,
                    fontWeight: 400,
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    lineHeight: { xs: 1.43, sm: 1.5 },
                  }}
                >
                  ご利用には、社内メールアドレスの登録が必要です。システム管理者にお問い合わせください。
                </Typography>
                <Typography
                  component='li'
                  variant='body1'
                  sx={{
                    color: theme.palette.text.primary,
                    fontWeight: 400,
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    lineHeight: { xs: 1.43, sm: 1.5 },
                  }}
                >
                  社内用の「@nohara-inc.co.jp」メールアドレスでログインしているか、ご確認ください。
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default NotAuthorizedRoute;
